/**
 * GM token management: quick-add form and per-token delete. Interactive
 * controls only — the machine-readable token mirror (§6.2 testids) lives in
 * tokenMirror.ts and is never duplicated here.
 */

import type { RoomState } from "@asarimitsuha/table-core";

export interface AddTokenCallbacks {
  onAddToken(name: string, ownerSeatId: string | null): void;
}

/** GM-only quick add: name + owner picker over the current seats. */
export function renderAddTokenForm(
  root: HTMLElement,
  state: RoomState,
  cb: AddTokenCallbacks,
): void {
  const form = document.createElement("form");
  form.dataset["testid"] = "add-token-form";

  const name = document.createElement("input");
  name.name = "tokenName";
  name.placeholder = "Token name";
  name.required = true;

  const owner = document.createElement("select");
  owner.name = "ownerSeatId";
  const gmOption = document.createElement("option");
  gmOption.value = "";
  gmOption.textContent = "GM-controlled";
  owner.appendChild(gmOption);
  for (const seat of Object.values(state.seats)) {
    if (seat.role === "gm") continue;
    const option = document.createElement("option");
    option.value = seat.id;
    option.textContent = seat.name;
    owner.appendChild(option);
  }

  const button = document.createElement("button");
  button.type = "submit";
  button.textContent = "Add token";

  form.append(name, owner, button);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    cb.onAddToken(name.value, owner.value === "" ? null : owner.value);
    name.value = "";
  });
  root.appendChild(form);
}

export interface DeleteTokenCallbacks {
  onDeleteToken(tokenId: string): void;
}

/** GM-only per-token delete list (compact; selection UX arrives with the
 *  inspector wiring). */
export function renderDeleteTokenList(
  root: HTMLElement,
  state: RoomState,
  cb: DeleteTokenCallbacks,
): void {
  const list = document.createElement("ul");
  list.dataset["testid"] = "token-delete-list";
  const tokens = Object.values(state.tokens).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  for (const token of tokens) {
    const item = document.createElement("li");
    item.textContent = token.name;
    const remove = document.createElement("button");
    remove.type = "button";
    remove.dataset["testid"] = "delete-token";
    remove.dataset["tokenId"] = token.id;
    remove.textContent = "✕";
    remove.title = `Delete ${token.name}`;
    remove.addEventListener("click", () => cb.onDeleteToken(token.id));
    item.appendChild(remove);
    list.appendChild(item);
  }
  root.appendChild(list);
}
